import { DecimalPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ChartConfiguration, ScriptableContext } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AgentsService } from '../../../orval-gen/endpoints/agents/agents.service';
import { ApplicationsService } from '../../../orval-gen/endpoints/applications/applications.service';
import { ConsumersService } from '../../../orval-gen/endpoints/consumers/consumers.service';
import { ProvidersService } from '../../../orval-gen/endpoints/providers/providers.service';
import { StatsService } from '../../../orval-gen/endpoints/stats/stats.service';
import {
  AgencyAgentsList200,
  AgencyAgentsList200DataItem,
  AgencyApplicationsList200DataItem,
  AgencyConsumerGet200Data,
  AgencyProvidersList200DataItem,
  AgencyStatsTimeline200DataItem,
} from '../../../orval-gen/models';
import { baseLineOptions, CHART, hoverLinePlugin } from '../../shared/chart-theme';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';

const CHART_DAYS = 30;

@Component({
  selector: 'app-dashboard',
  imports: [DecimalPipe, RouterLink, BaseChartDirective, IconComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private readonly ls = inject(LocalStorageService);
  private readonly agentsService = inject(AgentsService);
  private readonly providersService = inject(ProvidersService);
  private readonly consumersService = inject(ConsumersService);
  private readonly applicationsService = inject(ApplicationsService);
  private readonly statsService = inject(StatsService);

  readonly bpUrl = environment.bpUrl;
  readonly docsUrl = 'https://docs.airgateway.net';

  loaded = false;
  agencyName: string | null = null;
  isSuperagency = false;
  /** Applications are the consumer's, so only managers of its super agency may list them. */
  isConsumerOwner = false;
  agentAccounts: AgencyAgentsList200DataItem[] = [];
  /** Every active manager, where agentAccounts holds the first page of them. */
  managerCount = 0;
  showAllAgents = false;
  providers: AgencyProvidersList200DataItem[] = [];
  consumer: AgencyConsumerGet200Data | null = null;
  enabledApps = 0;
  availableApps = 0;
  bookedTotal = 0;
  issuedTotal = 0;

  readonly chartPlugins = [hoverLinePlugin];
  chartOptions = baseLineOptions();
  chartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };

  ngOnInit(): void {
    this.agencyName = this.ls.getItem('agency');
    this.isSuperagency = this.ls.getBool('isSuperagency');
    this.isConsumerOwner = this.ls.getBool('isConsumerOwner');
    this.loadAllDashboardData();
  }

  loadAllDashboardData(): void {
    this.loaded = false;
    const end = moment();
    const start = moment().subtract(CHART_DAYS - 1, 'days');

    forkJoin({
      // The route's maximum page size; the card shows three and expands to the rest.
      agents: this.agentsService
        .agencyAgentsList({ role: 'manager', active: 'true', limit: 50 })
        .pipe(
          catchError(error => {
            console.error('Failed to load agent accounts:', error);
            return of(null as AgencyAgentsList200 | null);
          })
        ),
      providers: this.providersService.agencyProvidersList().pipe(
        unwrap(),
        catchError(error => {
          console.error('Failed to load providers:', error);
          return of([] as AgencyProvidersList200DataItem[]);
        })
      ),
      consumer: this.consumersService.agencyConsumerGet().pipe(
        unwrap(),
        catchError(error => {
          console.error('Failed to load consumer data:', error);
          return of(null);
        })
      ),
      applications: this.isConsumerOwner
        ? this.applicationsService.agencyApplicationsList().pipe(
            unwrap(),
            catchError(error => {
              console.error('Failed to load applications:', error);
              return of([] as AgencyApplicationsList200DataItem[]);
            })
          )
        : of([] as AgencyApplicationsList200DataItem[]),
      timeline: this.statsService
        .agencyStatsTimeline({
          interval: 'day',
          date_start: start.format('YYYY-MM-DD'),
          date_end: end.format('YYYY-MM-DD'),
        })
        .pipe(
          unwrap(),
          catchError(error => {
            console.error('Failed to load stats timeline:', error);
            return of([] as AgencyStatsTimeline200DataItem[]);
          })
        ),
    })
      .pipe(finalize(() => (this.loaded = true)))
      .subscribe(({ agents, providers, consumer, applications, timeline }) => {
        this.agentAccounts = agents?.data ?? [];
        this.managerCount = agents?.metadata.total_records ?? this.agentAccounts.length;
        this.providers = providers ?? [];
        this.consumer = consumer;
        this.enabledApps = applications.filter(app => app.connection?.status === 'active').length;
        this.availableApps = applications.filter(app => !app.connection).length;
        this.buildChart(timeline ?? [], start);
      });
  }

  private buildChart(timeline: AgencyStatsTimeline200DataItem[], start: moment.Moment): void {
    const days: string[] = [];
    for (let i = 0; i < CHART_DAYS; i++) {
      days.push(start.clone().add(i, 'days').format('YYYY-MM-DD'));
    }
    const created = new Map<string, number>();
    const issued = new Map<string, number>();
    for (const item of timeline) {
      const day = item.bucket;
      if (item.type === 'created') created.set(day, (created.get(day) ?? 0) + item.count);
      if (item.type === 'issued') issued.set(day, (issued.get(day) ?? 0) + item.count);
    }
    this.bookedTotal = days.reduce((n, d) => n + (created.get(d) ?? 0), 0);
    this.issuedTotal = days.reduce((n, d) => n + (issued.get(d) ?? 0), 0);

    this.chartData = {
      labels: days.map(d => d.slice(5)),
      datasets: [
        {
          label: 'Created',
          data: days.map(d => created.get(d) ?? 0),
          borderColor: CHART.primary,
          pointHoverBorderColor: CHART.primary,
          fill: true,
          backgroundColor: (ctx: ScriptableContext<'line'>) => {
            const area = ctx.chart.chartArea;
            if (!area) return 'transparent';
            const gradient = ctx.chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
            gradient.addColorStop(0, 'rgba(178, 1, 10, 0.35)');
            gradient.addColorStop(1, 'rgba(178, 1, 10, 0)');
            return gradient;
          },
        },
        {
          label: 'Issued',
          data: days.map(d => issued.get(d) ?? 0),
          borderColor: CHART.foreground,
          pointHoverBorderColor: CHART.foreground,
          fill: false,
        },
      ],
    };
  }

  /** Whether agents see the provider: switched on by the agency and not switched off by AirGateway. */
  isLive(provider: AgencyProvidersList200DataItem): boolean {
    return provider.enabled && provider.active;
  }

  get activeProviders(): number {
    return this.providers.filter(p => this.isLive(p)).length;
  }

  get visibleAgents(): AgencyAgentsList200DataItem[] {
    return this.showAllAgents ? this.agentAccounts : this.agentAccounts.slice(0, 3);
  }

  get agencyInitials(): string {
    return (this.agencyName ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
  }

  get agentsUsed(): number {
    return this.consumer?.limits.agents.used ?? 0;
  }

  get agentLimit(): number {
    return this.consumer?.limits.agents.limit ?? 0;
  }

  get subagenciesUsed(): number {
    return this.consumer?.limits.subagencies.used ?? 0;
  }

  get subagencyLimit(): number {
    return this.consumer?.limits.subagencies.limit ?? 0;
  }

  /** Agents/subagencies allowance; a limit of 0 means "Unlimited" (same rule as before). */
  get agentLimitLabel(): string {
    return this.agentLimit === 0 ? 'Unlimited' : `${this.agentLimit}`;
  }

  get agentUsagePct(): number {
    return this.agentLimit ? Math.min(100, (this.agentsUsed / this.agentLimit) * 100) : 0;
  }

  get subagencyUnlimited(): boolean {
    return !!this.consumer?.limits.subagencies.enabled && this.subagencyLimit === 0;
  }

  get subagencyUsagePct(): number {
    return this.subagencyLimit
      ? Math.min(100, (this.subagenciesUsed / this.subagencyLimit) * 100)
      : 0;
  }

  toggleShowAll(): void {
    this.showAllAgents = !this.showAllAgents;
  }
}
